import { useRef, useState } from 'react';
import { Sparkles, Upload, FileText, Image, X, Loader, RotateCw, CloudOff, Landmark } from 'lucide-react';
import { STATUS_STYLES } from '../../components/AiReviewReport';
import { MAX_STATEMENT_FILES, runStatementCheck, uploadStatementFile, type StatementCheckState } from './statementCheck';

interface Props {
  contractTempId: string;
  state: StatementCheckState;
  onChange: (next: StatementCheckState) => void;
}

const DONE_TITLES = {
  ok: 'Nenhum empréstimo com as empresas monitoradas',
  attention: 'Confira o extrato',
  problem: 'Cliente pode ter outros empréstimos',
};

export default function StatementCheckCard({ contractTempId, state, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const running = state.phase === 'running';
  const busy = running || uploading > 0;

  const addFiles = async (list: FileList | null) => {
    if (!list?.length) return;
    setUploadError('');
    const picked = Array.from(list).slice(0, MAX_STATEMENT_FILES - state.files.length);
    if (picked.length < list.length) setUploadError(`Máximo de ${MAX_STATEMENT_FILES} arquivos de extrato.`);
    setUploading(n => n + picked.length);
    const results = await Promise.allSettled(picked.map(f => uploadStatementFile(f, contractTempId)));
    setUploading(n => n - picked.length);
    const ok = results.flatMap(r => (r.status === 'fulfilled' ? [r.value] : []));
    const failed = results.find(r => r.status === 'rejected') as PromiseRejectedResult | undefined;
    if (failed) setUploadError(failed.reason instanceof Error ? failed.reason.message : 'Erro no envio do arquivo.');
    if (ok.length) onChange({ phase: 'idle', files: [...state.files, ...ok] });
  };

  const removeFile = (url: string) => onChange({ phase: 'idle', files: state.files.filter(f => f.url !== url) });

  const analyze = async () => {
    const files = state.files;
    onChange({ phase: 'running', files });
    onChange(await runStatementCheck(files, contractTempId));
  };

  return (
    <div className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
        <Landmark className="w-4 h-4 text-sky-600" />
        <span className="text-sm font-semibold text-gray-700 flex-1">Checar outros empréstimos no extrato</span>
        <span className="text-[11px] font-medium text-gray-400">Opcional</span>
      </div>

      <div className="p-4 space-y-3">
        <p className="text-xs text-gray-500 leading-relaxed">
          Envie o extrato bancário do cliente (PDF ou fotos) e a IA procura movimentações com as empresas de empréstimo monitoradas, antes de seguir com o cadastro.
        </p>

        {state.files.length > 0 && (
          <ul className="space-y-1.5">
            {state.files.map(f => (
              <li key={f.url} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-xs">
                {f.type.startsWith('image/')
                  ? <Image className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  : <FileText className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />}
                <span className="flex-1 truncate font-medium text-gray-700">{f.name}</span>
                {!busy && (
                  <button
                    type="button"
                    onClick={() => removeFile(f.url)}
                    aria-label="Remover arquivo"
                    className="p-1 rounded text-gray-400 hover:bg-red-100 hover:text-red-600 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,application/pdf,.pdf"
          className="hidden"
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
        />

        {uploadError && <p className="text-xs text-red-600">{uploadError}</p>}

        <div className="flex flex-col sm:flex-row gap-2">
          {state.files.length < MAX_STATEMENT_FILES && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-gray-700 border-2 border-dashed border-gray-300 rounded-lg hover:border-sky-400 hover:bg-sky-50 transition-colors disabled:opacity-40"
            >
              {uploading > 0 ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading > 0 ? 'Enviando...' : state.files.length ? 'Adicionar páginas' : 'Anexar extrato'}
            </button>
          )}
          {state.files.length > 0 && (
            <button
              type="button"
              onClick={analyze}
              disabled={busy}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-sky-600 rounded-lg hover:bg-sky-700 transition-colors disabled:opacity-50"
            >
              {running ? <Loader className="w-4 h-4 animate-spin" /> : state.phase === 'done' ? <RotateCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
              {running ? 'Analisando extrato...' : state.phase === 'done' ? 'Analisar novamente' : 'Revisar extrato com IA'}
            </button>
          )}
        </div>

        {running && (
          <p className="text-xs text-sky-700">Lendo todas as movimentações. Isso costuma levar menos de 1 minuto.</p>
        )}

        {state.phase === 'failed' && (
          <div className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50">
            <CloudOff className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-700 leading-relaxed">{state.message}</p>
          </div>
        )}

        {state.phase === 'done' && (
          <div className={`p-3 rounded-lg border ${STATUS_STYLES[state.overall].banner}`}>
            <div className="flex items-center gap-2">
              {STATUS_STYLES[state.overall].icon}
              <p className="text-sm font-semibold">{DONE_TITLES[state.overall]}</p>
            </div>
            {state.messages.length > 0 && (
              <ul className="mt-2 pl-6 space-y-1">
                {state.messages.map((m, i) => (
                  <li key={i} className="text-xs leading-relaxed list-disc opacity-90">{m}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
